import { Box, Modal, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { SmauiCodePreview } from "@/ui-temp/core";
import { queryGetAd } from "@/Services/studio-query-v1";
import classes from "./ad-tags-modal.module.css";

const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className={classes.infoIcon}>
        <path d="M8.00065 14.6673C11.6825 14.6673 14.6673 11.6825 14.6673 8.00065C14.6673 4.31875 11.6825 1.33398 8.00065 1.33398C4.31875 1.33398 1.33398 4.31875 1.33398 8.00065C1.33398 11.6825 4.31875 14.6673 8.00065 14.6673Z" stroke="#787878" />
        <path d="M8.16732 11.334V8.00065C8.16732 7.68609 8.16732 7.5288 8.06955 7.43107C7.97185 7.33331 7.81452 7.33331 7.50065 7.33331" stroke="#787878" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7.99414 5.33398H8.00014" stroke="#787878" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PREVIEW_CLASSNAMES = {
    wrapper: classes.codeWrapper,
    header: classes.codeHeader,
    label: classes.codeLabel,
    root: classes.codeBox,
    docs: classes.codeDocs,
};

const GAM_DOCS_URL = "https://smartico.notion.site/How-to-run-your-Ad-in-Google-Ad-Manager-1d7141011e7780e2afdaf663ace9682c?pvs=4";
const DV360_DOCS_URL = "https://smartico.notion.site/How-to-run-your-Ad-in-Google-Display-Video-360-1d7141011e7780538f36ed048787a658?pvs=4";

export const useAdTagsModal = (ad) => {
    const { t } = useTranslation();
    const [opened, { open, close }] = useDisclosure(false);

    const queryAd = queryGetAd(ad?.id, undefined, { enabled: opened && Boolean(ad?.id) });

    const assets = queryAd?.data?.assets || {};

    const previewLabels = {
        docs: t('publish.howToUseScript'),
        copy: t('common.copy'),
        copied: t('common.copied'),
    };

    const modal = (
        <Modal
            opened={opened}
            onClose={close}
            size={660}
            radius={16}
            padding={0}
            title={t('ads.customTagsOf', { name: ad?.name || '' }).trim()}
            // Portaled, so every click in here still bubbles up the React tree to the
            // row's onClick and would open the ad preview modal behind this one.
            onClick={(event) => event.stopPropagation()}
            classNames={{
                content: classes.modalContent,
                header: classes.modalHeader,
                title: classes.modalTitle,
                close: classes.modalClose,
                body: classes.modalBody,
            }}
        >
            <Stack gap={16}>
                <Box className={classes.infoRow}>
                    <InfoIcon />
                    <Text className={classes.infoText}>
                        {t('booking.tag.adServingNotice')}
                    </Text>
                </Box>

                <Stack gap={12}>
                    <SmauiCodePreview
                        mt={6}
                        label="Google Ad Manager"
                        code={assets.ad_script_clickmacro || ""}
                        docsUrl={GAM_DOCS_URL}
                        language="javascript"
                        classNames={PREVIEW_CLASSNAMES}
                        labels={previewLabels}
                    />
                    <SmauiCodePreview
                        mt={6}
                        label="Google Display & Video 360"
                        code={assets.ad_script_clickmacro || ""}
                        docsUrl={DV360_DOCS_URL}
                        language="javascript"
                        classNames={PREVIEW_CLASSNAMES}
                        labels={previewLabels}
                    />
                    <SmauiCodePreview
                        mt={6}
                        label={t('publish.otherAdServer')}
                        code={assets.ad_script || ""}
                        docsUrl={DV360_DOCS_URL}
                        language="javascript"
                        classNames={PREVIEW_CLASSNAMES}
                        labels={previewLabels}
                    />
                </Stack>
            </Stack>
        </Modal>
    );

    return { open, modal };
};
